import { Action, Module, Mutation } from 'vuex-module-decorators'
import BaseStore from '@/store/BaseStore'
import {
  authApi,
  AuthUser,
  AuthUserGroup,
  AuthUserOperationMenu,
  AuthUserTopMenu,
  defaultAuthUser,
  logApi,
  LoginRequest,
  LogRequest,
  SystemBatchMenuDetail,
  SystemCode
} from '@/api'
import { cloneDeep } from 'lodash'
import dayjs from 'dayjs'

@Module({
  name: 'AuthUserStore',
  namespaced: true,
  stateFactory: true
})
export default class AuthUserStore extends BaseStore {
  _authUser: AuthUser = defaultAuthUser()

  _lastLoginAt: string = ''

  get authUser(): AuthUser {
    return this._authUser
  }

  get authUserGroup(): AuthUserGroup {
    return this._authUser.group
  }

  get authUserOperationMenu(): AuthUserOperationMenu {
    return this._authUser.operationMenu
  }

  get authUserTopMenu(): AuthUserTopMenu {
    return this._authUser.topMenu
  }

  get authUserBatchMenu(): SystemBatchMenuDetail[] {
    return this._authUser.batchMenu
  }

  get lastLoginAt(): string {
    return this._lastLoginAt
  }

  get isAuthUserGroupUserOnlyReference(): boolean {
    return this.authUserGroup.user === 2
  }

  get isAuthUserGroupBibliographyOnlyReference(): boolean {
    return this.authUserGroup.bibliography === 2
  }

  get isAdministrator(): boolean {
    return this._authUser.userId === 'Administrator'
  }

  @Mutation
  public setAuthUser(authUser: AuthUser) {
    this._authUser = cloneDeep(authUser)
  }

  @Mutation
  public clearAuthUser() {
    this._authUser = defaultAuthUser()
  }

  @Mutation
  public setLastLoginAt(lastLoginAt: string) {
    this._lastLoginAt = lastLoginAt
  }

  @Mutation
  public clearLastLoginAt() {
    this._lastLoginAt = ''
  }

  @Mutation
  public setAuthUserBranchCode(systemCode: SystemCode) {
    this._authUser.branchCode = systemCode
  }

  @Mutation
  public setAuthUserPlaceCode(systemCode: SystemCode) {
    this._authUser.placeCode = systemCode
  }

  @Action({ rawError: true })
  public async login(request: LoginRequest) {
    this.clearAuthUser()
    const authUser = await authApi.login(request)
    this.setAuthUser(authUser)
    this.setLastLoginAt(dayjs().format('YYYY/MM/DD HH:mm:ss'))
  }

  @Action({ rawError: true })
  public async logout() {
    await authApi.logout()
    this.clearAuthUser()
    this.clearLastLoginAt()
  }

  @Action({ rawError: true })
  public log(request: LogRequest) {
    logApi.log(request)
  }

  @Action({ rawError: true })
  public fetch(): Promise<void> {
    return new Promise(async resolve => {
      try {
        const authUser = await authApi.me()
        this.setAuthUser(authUser)
      } catch (error) {
        console.error(error)
        this.clearAuthUser()
        throw error
      } finally {
        resolve()
      }
    })
  }
}
